import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { subscribeToNotifications, Notification } from '@/services/realtimeNotifications';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Bell, Sparkles, CheckCircle2, AlertCircle } from 'lucide-react';

export const RealtimeToast = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user?.id) return;

    const unsubscribe = subscribeToNotifications(user.id, (notification: Notification) => {
      handleNewNotification(notification);
    });

    return () => {
      unsubscribe();
    };
  }, [user?.id]);

  const handleNewNotification = (notification: Notification) => {
    const icon = getNotificationIcon(notification.notification_type);
    
    toast(notification.title, {
      description: notification.message,
      icon: icon,
      duration: 5000,
      action: notification.action_url ? {
        label: 'View',
        onClick: () => {
          if (notification.action_url) {
            navigate(notification.action_url);
          }
        }
      } : undefined,
      className: getPriorityClassName(notification.priority),
    });

    // Play sound for high priority notifications
    if (notification.priority === 'high' || notification.priority === 'urgent') {
      playNotificationSound();
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'ai_generation':
        return <Sparkles className="h-4 w-4 text-cyan-500" />;
      case 'post_published':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'achievement':
        return <CheckCircle2 className="h-4 w-4 text-yellow-500" />;
      case 'system':
        return <Bell className="h-4 w-4 text-blue-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getPriorityClassName = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'border-l-4 border-red-500';
      case 'high':
        return 'border-l-4 border-orange-500';
      default:
        return '';
    }
  };

  const playNotificationSound = () => {
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIGWm98OScTgwOUKjl8LhjHAU7k9nyzn0xBSh+zPDhkj4JFV+16OyqVxQLR6Hh8r9vIgYtg9DyOB');
      audio.volume = 0.3;
      audio.play().catch(() => {});
    } catch (error) {
      // Ignore audio errors
    }
  };

  return null;
};
